import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
// eslint-disable-next-line no-restricted-imports
import { useAsObservableSource } from 'mobx-react-lite';
import { RaceSummaryTwoColumnsList } from 'src/domains/sportsbook/webview/components/raceSummary/templates/scrollMarkets/RaceSummaryWithScroll';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { RaceSummaryRowExtraPlaces } from './RaceSummaryRowExtraPlaces';
import { NotRunningPriceView } from 'src/domains/sportsbook/webview/components/raceSummary/utils/Utils';
import { ScrollRowWrapper } from 'src/domains/sportsbook/webview/components/raceSummary/templates/scrollMarkets/RaceSummaryWithScrollNew.style';
import {
    ExtraPlacesSelectionCast,
    ExtraPlacesRaceSummaryCaption,
    ExtraPlacesRunnerCaption,
    ExtraPlacesSelectionCaption,
} from './ExtraPlacesSummary.style';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import {
    ExtraPlacesSummaryPropsType,
    ExtraPlacesSummaryState,
    getSelectionByName,
} from 'src/domains/sportsbook/webview/components/raceSummary/raceSummarySport/extraPlacesSummary/ExtraPlacesSummary.state';
import { UsersState } from 'src/domains/players/state/UsersState';

function selectionsBuilder(
    selectionName: string,
    eachWayMarkets: MarketModel[],
    isNonRunnerRow: boolean,
    oddsType: 'f' | 'd',
    variant: DefaultComponentVariantType | null
): Array<JSX.Element | null> | null {
    return eachWayMarkets.map((market) => {
        const selectionInCurrentRow = getSelectionByName(selectionName, market);

        if (isNonRunnerRow === true) {
            return (
                <NotRunningPriceView
                    key={selectionInCurrentRow?.id2.toOldId()}
                    extraPlaces={true}
                    variant={variant}
                />
            );
        }

        if (selectionInCurrentRow === null) {
            return null;
        }

        return (
            <ExtraPlacesSelectionCast
                key={selectionInCurrentRow.id2.key}
                selectionId={selectionInCurrentRow.id2}
                title={oddsType === 'd' ? selectionInCurrentRow.price?.d : selectionInCurrentRow.price?.f}
                borderLeft={true}
                index={selectionInCurrentRow.id2.toOldId()}
            />
        );
    });
}

export const ExtraPlacesSummary = observer(
    'ExtraPlacesSummary',
    (propsIn: ExtraPlacesSummaryPropsType): JSX.Element | null => {
        const props = useAsObservableSource(propsIn);
        const common = useCommon();
        const configState = ConfigComponents.get(common).config;
        const usersState = UsersState.get(common);
        const { sportsbookVariant } = configState.componentVariants;

        const [state] = useState(() => new ExtraPlacesSummaryState(props));

        const selectionsByPrice = state.selectionsByPrice;
        if (selectionsByPrice === undefined) {
            return null;
        }

        const firstSelection = selectionsByPrice[0];
        if (firstSelection === undefined) {
            return null;
        }

        const marketModel = firstSelection.getMarket();
        if (marketModel === null) {
            return null;
        }
        const oddsTypeShort = usersState.oddsFormatShort;

        const StaticList = (): JSX.Element => {
            return (
                <>
                    <ExtraPlacesRaceSummaryCaption>
                        <ExtraPlacesRunnerCaption>
                            <I18n
                                langKey='events.race-summary.runner.label'
                                defaultText='Runner'
                            />
                        </ExtraPlacesRunnerCaption>
                    </ExtraPlacesRaceSummaryCaption>

                    {selectionsByPrice.map((selection) => {
                        return (
                            <RaceSummaryRowExtraPlaces
                                key={selection.id2.key}
                                isNonRunner={state.isNonRunnerInRow(selection.name)}
                                selectionId={selection.id2}
                            />
                        );
                    })}
                </>
            );
        };

        const ScrollList = (): JSX.Element => {
            return (
                <>
                    <ExtraPlacesRaceSummaryCaption>
                        {state.marketsPlaceAndReduction.map((market) => (
                            <ExtraPlacesSelectionCaption
                                key={market.marketId.key}
                                variant={sportsbookVariant}
                            >
                                {market.places}
                                <br />
                                {market.reduction}
                            </ExtraPlacesSelectionCaption>
                        ))}
                    </ExtraPlacesRaceSummaryCaption>
                    {selectionsByPrice.map((selection) => (
                        <ScrollRowWrapper
                            key={`price-row-${selection.id2.key}`}
                            isNonRunner={state.isNonRunnerInRow(selection.name)}
                            variant={sportsbookVariant}
                        >
                            {selectionsBuilder(
                                selection.name,
                                state.sortedMarketsByTermPlaces,
                                state.isNonRunnerInRow(selection.name),
                                oddsTypeShort,
                                sportsbookVariant
                            )}
                        </ScrollRowWrapper>
                    ))}
                </>
            );
        };

        return (
            <RaceSummaryTwoColumnsList
                staticListElements={StaticList()}
                scrollListElements={ScrollList()}
                columnCount={state.marketsPlaceAndReduction.length}
            />
        );
    }
);
